import { EmailInput } from "components";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import GuestlistAccessTypeDropdown from "components/dropdowns/reactselect/GuestlistAccessTypeDropdown";
import GuestlistBringsXDropdown from "components/dropdowns/reactselect/GuestlistBringsXDropdown";
import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { UserFirstLastName } from "UserAdmin/components/UserFirstLastName";
import { getGuestlistAccessTypeText } from "utilities/enumutils";
import { formatLongDateWithTime } from "utilities";

interface GuestFormState {
  firstName: string;
  lastName: string;
  bringsGuests: number;
  accessType: server.dto.GuestlistAccessType;
  email: string;
  mobile: string;
  passport: string;
  notes: string;
}

interface GuestSidePanelDetailsProps {
  guest: server.dto.Guest
  data: server.dto.GuestlistSidePanel
}

export const GuestSidePanelDetails = (props: GuestSidePanelDetailsProps) => {
  const [formState, setFormState] = useState<GuestFormState>({
    firstName: props.guest.name.firstName,
    lastName: props.guest.name.lastName,
    bringsGuests: props.guest.bringsGuests,
    accessType: props.data.accessTypes.find(at => at.guestlistAccessTypeId === props.guest.guestlistAccessTypeId),
    email: props.guest.emailAddress,
    mobile: props.guest.mobileNumber,
    passport: props.guest.passportNumber,
    notes: props.guest.notes,
  });

  const queryClient = useQueryClient();

  const bringGuestsEnabled = props.data.configuration.multipleGuestsPerName;
  const emailEnabled = props.data.configuration.guestEmailAddressFormElementBehavior === "visibleMandatory";
  const mobileEnabled = props.data.configuration.guestMobileNumberFormElementBehavior === "visibleMandatory";
  const passportEnabled = props.data.configuration.guestPassportNumberFormElementBehavior === "visibleMandatory";
  const notesEnabled = props.data.configuration.guestNotesFormElementBehavior !== "notVisible";
  const notesRequired = props.data.configuration.guestNotesFormElementBehavior === "visibleMandatory";
  const accessTypes = props.data.accessTypes ?? [];

  const onValueChanged = (key: string, value: any) => {
    if (key === "name") {
      const [firstName, lastName] = value as string[];
      setFormState((prevState) => ({
        ...prevState,
        firstName,
        lastName,
      }));
    } else if (key === "bringsGuest") {
      setFormState((prevState) => ({
        ...prevState,
        bringsGuests: value,
      }));
    } else if (key === "accessType") {
      setFormState((prevState) => ({
        ...prevState,
        accessType: value,
      }));
    } else if (key === "email") {
      setFormState((prevState) => ({
        ...prevState,
        email: value,
      }));
    } else if (key === "mobile") {
      setFormState((prevState) => ({
        ...prevState,
        mobile: value,
      }));
    } else if (key === "passport") {
      setFormState((prevState) => ({
        ...prevState,
        passport: value,
      }));
    } else if (key === "notes") {
      setFormState((prevState) => ({
        ...prevState,
        notes: value,
      }));
    }
  }

  const onSuccessfulUpdate = async () => {
    queryClient.invalidateQueries(["guestlist"]);
  }

  const getUpdateInstruction = (state: GuestFormState): server.dto.UpdateGuestInstruction => ({
    guestId: props.guest.guestId,
    bringsGuests: state.bringsGuests,
    emailAddress: state.email,
    guestlistAccessTypeId: state.accessType.guestlistAccessTypeId,
    mobileNumber: state.mobile,
    name: {
      first: state.firstName,
      last: state.lastName,
    },
    notes: state.notes,
    passportNumber: state.passport,
  });

  return <SidePanelDetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    fields={[{
      key: "section",
      type: "regular",
      title: RESX.Guestlist.Guest,
      fields: [{
        key: "name",
        label: RESX.GeneralLabels.Name,
        render: () => <div>{[formState.firstName, formState.lastName].filter(Boolean).join(" ")}</div>,
        inPageEdit: {
          type: "confirmOrCancelButton",
          fullWidth: true,
          initialValue: [formState.firstName, formState.lastName],
          render: ({ value, onChange, submit, cancel }) =>
            <UserFirstLastName
              focusOnMount
              firstName={value[0]}
              lastName={value[1]}
              onChange={(firstName, lastName) => onChange([firstName, lastName])}
              onEnter={submit}
              onEsc={cancel}
            />,
          submitChange: ([firstName, lastName]: string[]) => window.CoreApi.Guestlist.UpdateGuest(getUpdateInstruction({
            ...formState,
            firstName,
            lastName
          }))
        }
      }, {
        key: "bringsGuest",
        label: RESX.Guestlist.BringsGuestsLbl,
        hidden: !bringGuestsEnabled,
        render: () => <div>+{formState.bringsGuests}</div>,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<number>) => <GuestlistBringsXDropdown
            value={value}
            onChange={v => onChange(v, true)}
            options={[]}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />,
          type: "custom",
          initialValue: formState.bringsGuests,
          fullWidth: true,
          submitChange: (bringsGuests: number) => window.CoreApi.Guestlist.UpdateGuest(getUpdateInstruction({
            ...formState,
            bringsGuests
          }))
        }
      }, {
        key: "accessType",
        label: RESX.Guestlist.AccessType,
        render: () => <div>{getGuestlistAccessTypeText(formState.accessType)}</div>,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<server.dto.GuestlistAccessType>) => <GuestlistAccessTypeDropdown
            value={value}
            onChange={v => onChange(v, true)}
            options={accessTypes}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />,
          type: "custom",
          initialValue: formState.accessType,
          fullWidth: true,
          submitChange: (accessType: server.dto.GuestlistAccessType) => window.CoreApi.Guestlist.UpdateGuest(getUpdateInstruction({
            ...formState,
            accessType
          }))
        }
      }, {
        key: "email",
        label: RESX.Email.EmailAddress,
        hidden: !emailEnabled,
        render: () => <div>{formState.email}</div>,
        inPageEdit: {
          type: "confirmOrCancelButton",
          initialValue: formState.email,
          fullWidth: true,
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => (
            <EmailInput
              focusOnMount
              value={value}
              onChange={onChange}
              onEnter={submit}
              onEsc={cancel}
              emailCanBeBlank={false}
              validatorKey="email"
            />
          ),
          submitChange: (email: string) => window.CoreApi.Guestlist.UpdateGuest(
            getUpdateInstruction({ ...formState, email }))
        }
      }, {
        key: "mobile",
        label: RESX.Contact.Mobile,
        hidden: !mobileEnabled,
        render: () => <div>{formState.mobile}</div>,
        inPageEdit: {
          type: "confirmOrCancelButton",
          initialValue: formState.mobile,
          fullWidth: true,
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => (
            <RequiredTextInput
              validatorKey="mobile"
              focusOnMount
              maxLength={serverReference.constants.guestlist.maxMobileNumberLength}
              value={value}
              onChange={(mobile) => { onChange(mobile); }}
              onEnter={() => submit()}
              onEsc={() => cancel()}
            />
          ),
          submitChange: (mobile: string) => window.CoreApi.Guestlist.UpdateGuest(
            getUpdateInstruction({ ...formState, mobile }))
        }
      }, {
        key: "passport",
        label: RESX.Guestlist.Passport,
        hidden: !passportEnabled,
        render: () => <div>{formState.passport}</div>,
        inPageEdit: {
          type: "confirmOrCancelButton",
          initialValue: formState.passport,
          fullWidth: true,
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => (
            <RequiredTextInput
              validatorKey="passport"
              focusOnMount
              maxLength={serverReference.constants.guestlist.maxPassportNumberLength}
              value={value}
              onChange={(passport) => { onChange(passport); }}
              onEnter={() => submit()}
              onEsc={() => cancel()}
            />
          ),
          submitChange: (passport: string) => window.CoreApi.Guestlist.UpdateGuest(
            getUpdateInstruction({ ...formState, passport }))
        }
      }, {
        key: "notes",
        label: RESX.Note.Notes,
        isEmpty: !formState.notes,
        hidden: !notesEnabled,
        render: () => <div>{formState.notes}</div>,
        inPageEdit: {
          type: "confirmOrCancelButton",
          initialValue: formState.notes,
          fullWidth: true,
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => notesRequired ? (
            <RequiredTextInput
              validatorKey="notes"
              focusOnMount
              maxLength={serverReference.constants.guestlist.maxNotesLength}
              value={value}
              onChange={(notes) => { onChange(notes); }}
              onEnter={() => submit()}
              onEsc={() => cancel()}
            />
          ) : (
            <OptionalTextInput
              focusOnMount
              maxLength={serverReference.constants.guestlist.maxNotesLength}
              value={value}
              onChange={(notes) => { onChange(notes); }}
              onEnter={() => submit()}
              onEsc={() => cancel()}
            />
          ),
          submitChange: (notes: string) => window.CoreApi.Guestlist.UpdateGuest(
            getUpdateInstruction({ ...formState, notes }))
        }
      }, {
        key: "createdBy",
        label: RESX.RecordInformation.CreatedBy,
        render: () => <UserWithAvatar user={props.guest.addedByUser}>
          {formatLongDateWithTime(props.guest.addedOnUtc)}
        </UserWithAvatar>
      }]
    }]}
  />
}